// src/components/character_creation/NameAndBackground.jsx
// Полностью на Redux (characterDraft). Локального useState для name/legend/gender нет.
// Компонент читает draft из стора и пишет через patch.

import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Alert,
  Paper,
  Grid,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PsychologyIcon from '@mui/icons-material/Psychology';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import TransgenderIcon from '@mui/icons-material/Transgender';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';

import { useCharacterDraft } from '../../hooks/useCharacterDraft';
import {
  isLegendEmpty,
  legendToString,
  emptyLegend,
  normalizeLegend,
} from '../../utils/characterDraft';

const NameAndBackground = ({
  race,
  characterTemplate,
  temperament = 'adaptive',
}) => {
  const {
    draft,
    patch,
    setFieldValue,
    generateNameAndLegend,
    clearGeneration,
    generation,
  } = useCharacterDraft();

  // Только UI-локальное состояние для диалога редактора
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [legendDetailsVisible, setLegendDetailsVisible] = useState(false);

  const localName = draft?.name || '';
  const localGender = draft?.gender || 'neutral';
  const legendObject = normalizeLegend(draft?.background);

  const canGenerate = !!(race && characterTemplate);
  const hasBackgroundMemo = useMemo(
    () => !isLegendEmpty(legendObject),
    [legendObject]
  );

  const genders = [
    { value: 'male', label: 'Мужской', icon: <MaleIcon /> },
    { value: 'female', label: 'Женский', icon: <FemaleIcon /> },
    { value: 'neutral', label: 'Нейтральный', icon: <TransgenderIcon /> },
  ];

  // ---------- Генерация ----------

  const handleGenerateAll = async () => {
    if (!canGenerate) return;
    setLegendDetailsVisible(true);
    await generateNameAndLegend();
  };

  const handleClearAll = () => {
    patch({
      name: '',
      gender: 'neutral',
      background: emptyLegend(),
    });
    clearGeneration();
    setLegendDetailsVisible(false);
  };

  const handleClearName = () => setFieldValue('name', '');

  // ---------- Ручные правки ----------

  const handleNameChange = (e) => setFieldValue('name', e.target.value);
  const handleGenderChange = (e) => setFieldValue('gender', e.target.value);

  const handleOpenEditor = () => {
    setEditorContent(legendToString(legendObject));
    setEditorOpen(true);
  };

  const handleSaveEditor = () => {
    const text = editorContent.trim();
    if (text) {
      const updated = normalizeLegend({
        ...legendObject,
        description: text,
        background: legendObject.background || text,
      });
      patch({ background: updated });
    } else {
      patch({
        background: normalizeLegend({
          ...legendObject,
          description: '',
          background: '',
        }),
      });
    }
    setEditorOpen(false);
  };

  const handleCancelEditor = () => setEditorOpen(false);
  const handleEditorChange = (e) => setEditorContent(e.target.value);

  // ---------- Рендер ----------

  const renderSelectionInfo = () => {
    if (!race && !characterTemplate) return null;
    return (
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          Параметры генерации:
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {race && (
            <Chip
              label={`Раса: ${race.name}`}
              size="small"
              variant="outlined"
              color="primary"
              icon={<Box sx={{ fontSize: '1rem' }}>👽</Box>}
            />
          )}
          {characterTemplate && (
            <Chip
              label={`Шаблон: ${characterTemplate.name}`}
              size="small"
              variant="outlined"
              color="secondary"
              icon={<Box sx={{ fontSize: '1rem' }}>📋</Box>}
            />
          )}
          {temperament && (
            <Chip
              label={`Темперамент: ${temperament}`}
              size="small"
              variant="outlined"
              sx={{ bgcolor: 'action.selected' }}
              icon={<PsychologyIcon fontSize="small" />}
            />
          )}
        </Stack>
      </Paper>
    );
  };

  const renderLegendCard = () => {
    if (!legendDetailsVisible || !hasBackgroundMemo) return null;
    const {
      title,
      lineage,
      birthplace,
      childhood_memory,
      motivation,
      background: legendBackground,
      description,
    } = legendObject;

    return (
      <Card sx={{ mt: 3, borderLeft: 4, borderLeftColor: 'primary.main' }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FormatQuoteIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Детали сгенерированной легенды</Typography>
          </Box>
          <Grid container spacing={2}>
            {title && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Звание:</Typography>
                <Typography variant="body1">{title}</Typography>
              </Grid>
            )}
            {lineage && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Род:</Typography>
                <Typography variant="body1">{lineage}</Typography>
              </Grid>
            )}
            {birthplace && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Родина:</Typography>
                <Typography variant="body1">{birthplace}</Typography>
              </Grid>
            )}
            {childhood_memory && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">Память детства:</Typography>
                <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  "{childhood_memory}"
                </Typography>
              </Grid>
            )}
            {motivation && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Мотивация:</Typography>
                <Typography variant="body1">{motivation}</Typography>
              </Grid>
            )}
            {(legendBackground || description) && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary">Полная предыстория:</Typography>
                <Typography variant="body1" paragraph>
                  {legendBackground || description}
                </Typography>
              </Grid>
            )}
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setLegendDetailsVisible(false)} startIcon={<ClearIcon />}>
              Скрыть детали
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Основная информация</Typography>
      <Typography variant="body1" paragraph color="text.secondary">
        Дайте имя и описание вашему персонажу. Вы можете ввести их вручную или
        использовать автоматическую генерацию на основе выбранных расы, шаблона и темперамента.
      </Typography>

      {renderSelectionInfo()}

      {generation.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearGeneration}>
          {generation.error}
        </Alert>
      )}
      {generation.success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearGeneration}>
          {generation.success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          Автоматическая генерация:
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateAll}
            disabled={!canGenerate || generation.loading}
            startIcon={generation.loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
            size="small"
          >
            Сгенерировать всё
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleClearAll}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Очистить всё
          </Button>
        </Stack>
        {!canGenerate && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            Для генерации необходимо выбрать расу и шаблон на предыдущих шагах
          </Typography>
        )}
        {canGenerate && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Генерация основана на: {race?.name}, {characterTemplate?.name}, {temperament}
          </Typography>
        )}
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Гендер персонажа</InputLabel>
            <Select value={localGender} onChange={handleGenderChange} label="Гендер персонажа">
              {genders.map((g) => (
                <MenuItem key={g.value} value={g.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 1 }}>{g.icon}</Box>
                    {g.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Имя персонажа"
            value={localName}
            onChange={handleNameChange}
            placeholder="Введите имя персонажа или сгенерируйте автоматически"
            InputProps={{
              endAdornment: localName ? (
                <Tooltip title="Очистить имя">
                  <IconButton size="small" onClick={handleClearName}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null,
            }}
            helperText="Имя будет использоваться во всех игровых взаимодействиях"
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleOpenEditor}
            startIcon={<EditIcon />}
            sx={{ minHeight: 56, justifyContent: 'flex-start', px: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {hasBackgroundMemo ? 'Редактировать предысторию' : 'Добавить предысторию'}
            </Typography>
          </Button>
        </Grid>
      </Grid>

      {renderLegendCard()}

      <Paper sx={{ p: 2, mt: 3, bgcolor: 'background.default' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Имя заполнено:
            </Typography>
            <Chip
              size="small"
              label={localName ? 'Да' : 'Нет'}
              color={localName ? 'success' : 'error'}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Предыстория заполнена:
            </Typography>
            <Chip
              size="small"
              label={hasBackgroundMemo ? 'Да' : 'Нет'}
              color={hasBackgroundMemo ? 'success' : 'error'}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={editorOpen}
        onClose={handleCancelEditor}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
      >
        <DialogTitle>
          Редактор предыстории
          <Typography variant="body2" color="text.secondary">
            Используйте Markdown для форматирования
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={15}
            value={editorContent}
            onChange={handleEditorChange}
            placeholder="Введите или отредактируйте предысторию вашего персонажа..."
            variant="outlined"
            sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '14px' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEditor}>Отмена</Button>
          <Button onClick={handleSaveEditor} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NameAndBackground;
